// Pure conversions for the Parliament chapter: what a Coro document's
// detail says beyond the news (type, chamber, committee meeting, the
// source's updates). No fetching, no React; tested in
// tests/adapt-parliament.test.ts. `fields` is read with type guards: its
// keys are Coro's and the contract types them as unknown.
import type { components } from "../../api/schema";
import { formatClock, formatLongDate, formatShortDate, formatTime, formatWeekdayDate, romeDay } from "../../lib/dates";
import type { NewsItem } from "../../data/mock";
import {
  cardLinks,
  charCount,
  itemPath,
  sourceBadge,
  toNewsItem,
  toReaderBlocks,
  type FeedItem,
  type FeedSitting,
  type SourceTree,
} from "../news/adapt";
import { fromOf } from "../../lib/navigation";
import { readNewsParams, writeNewsParams } from "../news/params";

export type ItemDetail = components["schemas"]["ItemDetail"];

/**
 * The Italian label of a Coro `item_type` (Coro's `resource`, ADR 0015-0018):
 * a value not listed here is shown as it is, so a new resource reads
 * raw rather than wrong.
 */
export const DOCUMENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  stenografico: "Resoconto stenografico",
  resoconto_in_corso: "Resoconto in corso di seduta",
  comunicato: "Comunicato di commissione",
  resoconto_sommario: "Resoconto sommario",
  allegato_a: "Allegato A",
  allegato_b: "Allegato B",
  allegato_b_atti_modificati: "Allegato B · Atti modificati",
  odg_giorno: "Ordine del giorno",
  ordine_del_giorno: "Ordine del giorno",
  allegato_odg: "Allegato all’ordine del giorno",
  testo_atto: "Testo dell’atto",
  emendamenti: "Emendamenti",
  relazione: "Relazione",
  dossier: "Dossier",
  bollettino_pdf: "Bollettino (solo PDF)",
  ordine_del_giorno_commissione: "Ordine del giorno di commissione",
};

/** The label of an item type, or the type itself when it is not a known one. */
export function documentTypeLabel(itemType: string): string {
  return DOCUMENT_TYPE_LABELS[itemType] ?? itemType;
}

const CHAMBER_LABELS: Readonly<Record<string, string>> = { camera: "Camera", senato: "Senato" };

/** «Camera» / «Senato»; another value as it is. */
export function chamberLabel(chamber: string): string {
  return CHAMBER_LABELS[chamber.toLowerCase()] ?? chamber;
}

/** The kinds of a committee meeting at the Senato (`fields.sitting_kind`), as the row names them. */
const SITTING_KINDS: Readonly<Record<string, string>> = {
  seduta: "Seduta",
  ufficio_di_presidenza: "Ufficio di Presidenza",
  sottocommissione: "Sottocommissione",
  comitato_ristretto: "Comitato ristretto",
};

// ---- fields, read with type guards ----------------------------------------------

type Fields = NonNullable<ItemDetail["fields"]>;

/** A non-blank string under the key, or null. */
function text(fields: Fields, key: string): string | null {
  const value = fields[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** An integer under the key (a number, or a string of digits), or null. */
function integer(fields: Fields, key: string): number | null {
  const value = fields[key];
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return Number(value.trim());
  return null;
}

/** An http(s) URL under the key, or null: anything else is not a link to offer. */
function url(fields: Fields, key: string): string | null {
  const value = text(fields, key);
  return value !== null && /^https?:\/\//i.test(value) ? value : null;
}

// ---- the section «Parlamento» of the page ------------------------------------------

/** One row of the section: «Tipo», «Resoconto sommario». */
export interface ParliamentFact {
  label: string;
  value: string;
}

/** A link of the section, as a row with «Apri»: «Apri il PDF del bollettino», «Scheda dell'atto». */
export interface ParliamentLink {
  label: string;
  href: string;
}

export interface ParliamentFacts {
  facts: ParliamentFact[];
  links: ParliamentLink[];
  /** What the source's revisions mean for this document (updateLabel); null when there is nothing to say. */
  update: string | null;
}

/**
 * The committee meeting as one line: «Seduta n. 304 · Pomeridiana ·
 * 14:05-15:10», from what `fields` carries (the Senato's bulletins; the
 * Camera's communiqués have only the committee). Null without any of it.
 */
export function meetingLabel(fields: Fields): string | null {
  const kind = text(fields, "sitting_kind");
  const number = integer(fields, "sitting_number");
  const shift = text(fields, "sitting_shift");
  const started = text(fields, "sitting_started_time");
  const ended = text(fields, "sitting_ended_time");
  const parts: string[] = [];
  const kindLabel = kind ? (SITTING_KINDS[kind] ?? kind) : number !== null ? "Seduta" : null;
  if (kindLabel) parts.push(number !== null ? `${kindLabel} n. ${number}` : kindLabel);
  if (shift) parts.push(shift);
  if (started && ended) parts.push(`${started}-${ended}`);
  else if (started) parts.push(`dalle ${started}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** «alle HH:MM del dd/mm/yyyy», in Europe/Rome. */
const atTimeOnDay = (iso: string) => `alle ${formatTime(iso)} del ${formatShortDate(iso)}`;

/** «il dd/mm/yyyy alle HH:MM», in Europe/Rome. */
const onDayAtTime = (iso: string) => `il ${formatShortDate(iso)} alle ${formatTime(iso)}`;

/** The documents that grow during the sitting: a new version is growth, not a correction (analisi-coro.md §3.5). */
const GROWING_TYPES = new Set(["stenografico", "resoconto_in_corso"]);

/**
 * What `revision_count` means, in words. For a stenografico or a resoconto
 * in corso: «N aggiornamenti dalla fonte, l'ultimo alle HH:MM del
 * dd/mm/yyyy» (the record grew at nearly every run). For every other
 * document a new version is a correction by the source: «Corretto dalla
 * fonte il …» (once) or «Corretto dalla fonte N volte, l'ultima il …».
 * Null with no revision. The instant is the last of `revised_at`, or
 * `last_changed_at` when the list is empty.
 */
export function updateLabel(
  detail: Pick<ItemDetail, "item_type" | "revision_count" | "revised_at" | "last_changed_at">,
): string | null {
  const n = detail.revision_count;
  if (n <= 0) return null;
  const last = detail.revised_at.at(-1) ?? detail.last_changed_at;
  const when = last ? ` ${atTimeOnDay(last)}` : "";
  if (detail.item_type && GROWING_TYPES.has(detail.item_type)) {
    const head = `${n} ${plural(n, "aggiornamento", "aggiornamenti")} dalla fonte`;
    return last ? `${head}, l’ultimo${when}` : head;
  }
  if (n === 1) return last ? `Corretto dalla fonte ${onDayAtTime(last)}` : "Corretto dalla fonte";
  return last ? `Corretto dalla fonte ${n} volte, l’ultima ${onDayAtTime(last)}` : `Corretto dalla fonte ${n} volte`;
}

/**
 * The section «Parlamento» of a Coro document's page: the type, the chamber,
 * the committee and its meeting, the links the fields offer (the bulletin's
 * PDF, the act's card), and the source's updates. Nothing of it is made up:
 * a row is there only when the detail carries it.
 */
export function toParliamentFacts(
  detail: Pick<ItemDetail, "item_type" | "chamber" | "fields" | "revision_count" | "revised_at" | "last_changed_at">,
): ParliamentFacts {
  const facts: ParliamentFact[] = [];
  const links: ParliamentLink[] = [];
  if (detail.item_type) facts.push({ label: "Tipo", value: documentTypeLabel(detail.item_type) });
  if (detail.chamber) facts.push({ label: "Ramo", value: chamberLabel(detail.chamber) });
  const fields = detail.fields ?? {};
  const committee = text(fields, "committee");
  if (committee) facts.push({ label: "Commissione", value: committee });
  const meeting = meetingLabel(fields);
  if (meeting) facts.push({ label: "Riunione", value: meeting });
  const bulletin = url(fields, "bulletin_pdf_url");
  if (bulletin) links.push({ label: "Apri il PDF del bollettino", href: bulletin });
  const actCard = url(fields, "act_card_url");
  if (actCard) links.push({ label: "Scheda dell’atto", href: actCard });
  return { facts, links, update: updateLabel(detail) };
}

/** Whether the section has anything to show. */
export function hasParliamentFacts({ facts, links, update }: ParliamentFacts): boolean {
  return facts.length > 0 || links.length > 0 || update !== null;
}

// ---- the index and the reader ------------------------------------------------------

export type Section = components["schemas"]["Section"];
export type SectionLevel = Section["level"];

/** What the index lists besides the consultants' tree: the routine entries, the speeches, the notes. */
export interface IndexFilter {
  routine: boolean;
  speeches: boolean;
  notes: boolean;
}

/**
 * The index as it opens: the topics and the items, routine ones too (D3 of
 * the brief: Coro's flag does not match what Politor hides, so nothing is
 * hidden until it does; the switch turns them off), no speech, no note.
 */
export const DEFAULT_INDEX_FILTER: IndexFilter = { routine: true, speeches: false, notes: false };

/** One entry of the index as SectionIndex shows it: flattened, with its depth in the tree shown. */
export interface IndexEntry {
  ordinal: number;
  level: SectionLevel;
  /** The title, or the speaker with their role for a speech. */
  label: string;
  /** How many entries stand above it in the index shown (0 for a root). */
  depth: number;
  /** Whether «vai al punto» can reach it: it has a position in the text, and the reader shows it. */
  reachable: boolean;
  /**
   * The entry is not reachable though it stands in the text: the tooltip
   * «Posizione non disponibile» is for it, not for the root `part` that names
   * the document and has no position by nature.
   */
  positionMissing: boolean;
  /** The id of the entry in the official page (the Camera's), for the jump at `#anchor`. */
  anchor: string | null;
}

/** Whether an entry passes the filter on its own (its parents aside). */
function passes(section: Section, filter: IndexFilter): boolean {
  if (section.in_routine && !filter.routine) return false;
  if (section.level === "speech" && !filter.speeches) return false;
  if (section.level === "note" && !filter.notes) return false;
  return true;
}

/** «ASCANI Anna · PRESIDENTE»: the speaker, then the role and the group when there; the title without a speaker. */
export function speakerHeading(section: Pick<Section, "title" | "speaker" | "speaker_role" | "speaker_group">): string {
  if (!section.speaker) return section.title;
  const extra = [section.speaker_role, section.speaker_group].filter(
    (part): part is string => typeof part === "string" && part.trim() !== "" && part !== section.speaker,
  );
  return [section.speaker, ...extra].join(" · ");
}

/**
 * The index as a flat list in reading order (`ordinal`), the tree from
 * `parent_ordinal`. An entry is listed when it passes the filter and every
 * entry above it is listed too: a note under a speech that is out goes out
 * with it («fuori con lui»), an item under a routine topic as well. The
 * depth is the one in the list, so what is shown never has a gap. An entry
 * is reachable when it has a position and, with `shown` (the ordinals the
 * reader's pages hold, shownOrdinals), when the reader shows it: an index
 * that points past the text in the cache (a record that grew since the text
 * was served) has nothing to go to.
 */
export function sectionTree(
  sections: Section[],
  filter: IndexFilter = DEFAULT_INDEX_FILTER,
  shown: ReadonlySet<number> | null = null,
): IndexEntry[] {
  const ordered = [...sections].sort((a, b) => a.ordinal - b.ordinal);
  const depthOf = new Map<number, number>();
  const out: IndexEntry[] = [];
  for (const section of ordered) {
    const parentDepth =
      section.parent_ordinal === null
        ? -1
        : depthOf.has(section.parent_ordinal)
          ? depthOf.get(section.parent_ordinal)!
          : null;
    // an orphan (its parent is not listed) is out, as its parent is
    if (parentDepth === null || !passes(section, filter)) continue;
    const depth = parentDepth + 1;
    depthOf.set(section.ordinal, depth);
    const reachable = section.char_start !== null && (shown === null || shown.has(section.ordinal));
    out.push({
      ordinal: section.ordinal,
      level: section.level,
      label: section.level === "speech" ? speakerHeading(section) : section.title,
      depth,
      reachable,
      positionMissing: section.char_start === null && !(section.level === "part" && section.parent_ordinal === null),
      anchor: section.anchor,
    });
  }
  return out;
}

/** The ordinals of the entries the reader's pages hold, for sectionTree's `shown`. */
export function shownOrdinals(pages: ReaderPage[]): Set<number> {
  const out = new Set<number>();
  for (const page of pages) for (const section of page.sections) if (section.ordinal !== null) out.add(section.ordinal);
  return out;
}

/** The id of an entry's element in the reader, for «vai al punto». */
export const sectionAnchorId = (ordinal: number) => `section-${ordinal}`;

/**
 * The UTF-16 index of each code-point position, in one pass over the text:
 * the api counts in code points (PostgreSQL, Python), JavaScript in UTF-16
 * units, and a character outside the Basic Multilingual Plane («𝒜») takes
 * two units. `positions` must be sorted ascending; one past the end clamps
 * to the text's length.
 */
export function codePointOffsets(text: string, positions: number[]): number[] {
  const out: number[] = [];
  let unit = 0;
  let point = 0;
  for (const target of positions) {
    while (point < target && unit < text.length) {
      const code = text.charCodeAt(unit);
      unit += code >= 0xd800 && code <= 0xdbff && unit + 1 < text.length ? 2 : 1;
      point++;
    }
    out.push(unit);
  }
  return out;
}

/** A piece of the text: from an entry's start to the next entry's; `ordinal` null for the text before the first. */
export interface TextPiece {
  ordinal: number | null;
  text: string;
}

/**
 * The text cut at the start of every entry that has a position, in code
 * points. An entry's piece runs to the next entry's start (its `char_end`
 * may leave a gap: the gap stays with it). Two entries at one position are
 * one piece, the first one's. Entries without a position cut nothing.
 */
export function splitAtSections(text: string, sections: Pick<Section, "ordinal" | "char_start">[]): TextPiece[] {
  const positioned = sections
    .filter((s): s is typeof s & { char_start: number } => s.char_start !== null && s.char_start >= 0)
    .sort((a, b) => a.char_start - b.char_start || a.ordinal - b.ordinal);
  const starts: { ordinal: number; point: number }[] = [];
  for (const s of positioned) {
    if (starts.length > 0 && starts[starts.length - 1]!.point === s.char_start) continue;
    starts.push({ ordinal: s.ordinal, point: s.char_start });
  }
  const units = codePointOffsets(
    text,
    starts.map((s) => s.point),
  );
  const pieces: TextPiece[] = [];
  const first = units[0] ?? text.length;
  if (first > 0 || starts.length === 0) pieces.push({ ordinal: null, text: text.slice(0, first) });
  for (let i = 0; i < starts.length; i++) {
    const from = units[i]!;
    const to = i + 1 < units.length ? units[i + 1]! : text.length;
    if (from >= text.length) break;
    pieces.push({ ordinal: starts[i]!.ordinal, text: text.slice(from, to) });
  }
  return pieces;
}

/** A character the comparison of a title with the text ignores: blanks, the zero-width space, the soft hyphen. */
const IGNORED = /[\s\u200b\u00ad]/;

/**
 * The piece without the title it begins with, when it does: the entry's
 * title is written as a heading by the reader, so the text does not repeat
 * it. The comparison ignores blanks, case, U+200B and U+00AD (the title is
 * normalised, the text is not). The piece as it is when it does not begin
 * with the title.
 */
export function stripTitle(piece: string, title: string): string {
  const wanted = [...title]
    .filter((ch) => !IGNORED.test(ch))
    .join("")
    .toLowerCase();
  if (wanted === "") return piece;
  let matched = 0;
  let i = 0;
  while (i < piece.length && matched < wanted.length) {
    const ch = piece[i]!;
    const code = piece.codePointAt(i)!;
    const size = code > 0xffff ? 2 : 1;
    const char = piece.slice(i, i + size);
    if (!IGNORED.test(ch)) {
      const lower = char.toLowerCase();
      if (wanted.startsWith(lower, matched)) matched += lower.length;
      else return piece;
    }
    i += size;
  }
  if (matched < wanted.length) return piece;
  // a punctuation mark the source puts after a heading goes with it («PRESIDENTE.»)
  return piece.slice(i).replace(/^[\s.:]*\n?/, "");
}

/** How much text a page of the reader holds, about; an entry's piece is never split between pages. */
export const READER_PAGE_CHARS = 40_000;

/** One entry's text as the reader shows it. */
export interface ReaderSection {
  /** Null for the text before the first entry. */
  ordinal: number | null;
  level: SectionLevel | null;
  /** The heading above the text: the title of a topic or an item, the speaker of a speech; null for a note and the preamble. */
  heading: string | null;
  /** The role and the group of the speaker, under the name; null for anything but a speech. */
  speakerDetail: string | null;
  blocks: string[];
  /** The characters of the blocks, in code points. */
  chars: number;
}

export interface ReaderPage {
  sections: ReaderSection[];
  chars: number;
}

/** The paragraphs of a text: the news reader's cut (toReaderBlocks) unless a test hands another. */
export type Blocks = (text: string) => string[];

/**
 * The pieces of the text as the reader's sections, in pages of about
 * READER_PAGE_CHARS code points: a section never spans two pages, so a
 * page may run past the limit by one section. A topic or an item has its
 * title as heading (and the text does not repeat it); a speech has its
 * speaker, with role and group under the name; a note and the preamble
 * have no heading. `blocks` cuts a piece into paragraphs.
 */
export function readerPages(
  pieces: TextPiece[],
  sections: Section[],
  blocks: Blocks = toReaderBlocks,
  pageChars = READER_PAGE_CHARS,
): ReaderPage[] {
  const byOrdinal = new Map(sections.map((s) => [s.ordinal, s]));
  const pages: ReaderPage[] = [];
  let current: ReaderPage = { sections: [], chars: 0 };
  for (const piece of pieces) {
    const section = piece.ordinal === null ? null : (byOrdinal.get(piece.ordinal) ?? null);
    let heading: string | null = null;
    let speakerDetail: string | null = null;
    let text = piece.text;
    if (section && section.level === "speech") {
      heading = section.speaker ?? section.title;
      const detail = [section.speaker_role, section.speaker_group].filter(
        (part): part is string => typeof part === "string" && part.trim() !== "" && part !== heading,
      );
      speakerDetail = detail.length > 0 ? detail.join(" · ") : null;
    } else if (section && section.level !== "note") {
      heading = section.title;
      text = stripTitle(text, section.title);
    }
    const paragraphs = blocks(text);
    const chars = paragraphs.reduce((sum, block) => sum + charCount(block), 0);
    if (paragraphs.length === 0 && heading === null) continue;
    const entry: ReaderSection = {
      ordinal: piece.ordinal,
      level: section?.level ?? null,
      heading,
      speakerDetail,
      blocks: paragraphs,
      chars,
    };
    if (current.sections.length > 0 && current.chars + chars > pageChars) {
      pages.push(current);
      current = { sections: [], chars: 0 };
    }
    current.sections.push(entry);
    current.chars += chars;
  }
  if (current.sections.length > 0) pages.push(current);
  return pages;
}

/** The page (its index) that holds the entry, for «vai al punto»; -1 when none does. */
export function pageOfSection(pages: ReaderPage[], ordinal: number): number {
  return pages.findIndex((page) => page.sections.some((s) => s.ordinal === ordinal));
}

// ---- what the page says of a document's text and index ------------------------------

/** What stands above the text of a Coro document instead of the generic notices. */
export type ParliamentNotice = "pdf-bulletin" | "no-text" | "index-later";

/** The records whose index arrives after the text (the Camera's with the closed sitting, the Senato's as it grows). */
const RECORD_TYPES = new Set(["stenografico", "resoconto_in_corso"]);

/**
 * The notices of a Coro document: «Bollettino solo in PDF» for a
 * `bollettino_pdf`; «Testo non disponibile: apri il PDF» for a PDF without
 * extractable text (`text_policy` no_text); «Indice disponibile a fine
 * seduta» for a record with a text and no entry yet (`sections` [] once it
 * has answered; null while on its way says nothing). The first two replace
 * the generic «Testo non disponibile» (`replacesMissing`).
 */
export function parliamentNotices(
  detail: Pick<ItemDetail, "item_type" | "text_policy" | "has_text" | "text">,
  sections: Pick<Section, "ordinal">[] | null,
): { notices: ParliamentNotice[]; replacesMissing: boolean } {
  const notices: ParliamentNotice[] = [];
  if (detail.item_type === "bollettino_pdf") notices.push("pdf-bulletin");
  else if (detail.text_policy === "no_text") notices.push("no-text");
  const hasText = detail.has_text && Boolean(detail.text?.trim());
  if (hasText && sections !== null && sections.length === 0 && detail.item_type && RECORD_TYPES.has(detail.item_type))
    notices.push("index-later");
  return { notices, replacesMissing: notices.includes("pdf-bulletin") || notices.includes("no-text") };
}

// ---- the sittings ---------------------------------------------------------------

export type Sitting = components["schemas"]["Sitting"];
export type SittingPage = components["schemas"]["SittingPage"];
export type SittingDetail = components["schemas"]["SittingDetail"];
export type Chamber = Sitting["chamber"];
export type SessionType = Sitting["session_type"];

export const CHAMBERS: readonly Chamber[] = ["camera", "senato"];
export const SESSION_TYPES: readonly SessionType[] = ["plenaria", "commissioni"];
const isChamber = (value: string): value is Chamber => CHAMBERS.some((c) => c === value);
const isSessionType = (value: string): value is SessionType => SESSION_TYPES.some((t) => t === value);

const SESSION_TYPE_LABELS: Record<SessionType, string> = { plenaria: "Plenaria", commissioni: "Commissioni" };
export const sessionTypeLabel = (type: SessionType) => SESSION_TYPE_LABELS[type];

/** The documents of a bulletin, by chamber: the Camera publishes communiqués, the Senato summary records. */
const BULLETIN_DOCUMENTS: Record<Chamber, [string, string]> = {
  camera: ["comunicato", "comunicati"],
  senato: ["resoconto", "resoconti"],
};

/**
 * The key of a sitting's page, from the route's parameters: null when they
 * are not a chamber, a session type and a positive integer (no call is made).
 */
export function parseSittingKey(
  chamber: string,
  sessionType: string,
  number: string,
): { chamber: Chamber; sessionType: SessionType; number: number } | null {
  if (!isChamber(chamber) || !isSessionType(sessionType) || !/^[1-9]\d{0,5}$/.test(number)) return null;
  return { chamber, sessionType, number: Number(number) };
}

/** The path of a sitting's page: /parlamento/<chamber>/<session_type>/<number>. */
export const sittingPath = (sitting: Pick<Sitting, "chamber" | "session_type" | "number">) =>
  `/parlamento/${sitting.chamber}/${sitting.session_type}/${sitting.number}`;

/** «Seduta n. 467» for the plenary, «Bollettino n. 678» for the committees. */
export function sittingNumberLabel(sitting: Pick<Sitting, "session_type" | "number">): string {
  return sitting.session_type === "commissioni" ? `Bollettino n. ${sitting.number}` : `Seduta n. ${sitting.number}`;
}

/**
 * The state with its time, as the source wrote it. The plenary: «Convocata alle
 * 11:00» («Convocata, nessun resoconto» when the day has passed), «In corso
 * dalle 10:04», «Terminata alle 18:54» («Terminata, ora in arrivo» while the
 * source has not written the time; with the day when it ended after
 * midnight: «Terminata alle 06:55 del 24/12/2022»). A bulletin has no times
 * and no state of its own: «Bollettino n. 678 · 19 resoconti».
 */
export function sittingStatusLabel(
  sitting: Pick<
    Sitting,
    | "chamber"
    | "session_type"
    | "number"
    | "sitting_on"
    | "status"
    | "scheduled_time"
    | "started_time"
    | "ended_time"
    | "ended_on"
    | "documents_count"
  >,
  today: string,
): string {
  if (sitting.session_type === "commissioni") {
    const n = sitting.documents_count;
    const [one, many] = BULLETIN_DOCUMENTS[sitting.chamber];
    return `${sittingNumberLabel(sitting)} · ${n} ${n === 1 ? one : many}`;
  }
  switch (sitting.status) {
    case "convocata":
      if (sitting.sitting_on < today) return "Convocata, nessun resoconto";
      return sitting.scheduled_time ? `Convocata alle ${formatClock(sitting.scheduled_time)}` : "Convocata";
    case "in_corso":
      return sitting.started_time ? `In corso dalle ${formatClock(sitting.started_time)}` : "In corso";
    case "terminata": {
      if (!sitting.ended_time) return "Terminata, ora in arrivo";
      const at = `Terminata alle ${formatClock(sitting.ended_time)}`;
      return sitting.ended_on && sitting.ended_on !== sitting.sitting_on
        ? `${at} del ${formatShortDate(sitting.ended_on)}`
        : at;
    }
  }
}

/** One row of a list of sittings, as Ultime news shows it. */
export interface SittingRow {
  key: string;
  path: string;
  /** «14 ottobre 2026 · Plenaria · Seduta n. 727 · Terminata alle 20:00 · 6 documenti». */
  text: string;
  /** The sitting is open now: the row stands out as the current stage of an iter does. */
  live: boolean;
}

const documentsLabel = (n: number) => `${n} ${n === 1 ? "documento" : "documenti"}`;

/** The sittings as rows, in the order served (newest day first). */
export function toSittingRows(sittings: Sitting[], today: string): SittingRow[] {
  return sittings.map((sitting) => {
    const parts = [formatLongDate(sitting.sitting_on), sessionTypeLabel(sitting.session_type)];
    if (sitting.session_type === "commissioni") parts.push(sittingStatusLabel(sitting, today));
    else
      parts.push(
        sittingNumberLabel(sitting),
        sittingStatusLabel(sitting, today),
        documentsLabel(sitting.documents_count),
      );
    return {
      key: sittingPath(sitting),
      path: sittingPath(sitting),
      text: parts.join(" · "),
      live: sitting.status === "in_corso",
    };
  });
}

/** How often the sittings are asked again: every minute while a sitting of the day is open, else every five. */
export const POLL_LIVE_MS = 60_000;
export const POLL_IDLE_MS = 300_000;

/** Whether a sitting of `today` is open. */
export function hasLiveSitting(sittings: Pick<Sitting, "sitting_on" | "status">[], today: string): boolean {
  return sittings.some((s) => s.sitting_on === today && s.status === "in_corso");
}

/**
 * The polling interval of the sittings: POLL_LIVE_MS with a sitting of the
 * day in progress (Coro writes every ten minutes in sitting hours), POLL_IDLE_MS
 * otherwise. The hooks never poll in the background.
 */
export function pollingInterval(sittings: Pick<Sitting, "sitting_on" | "status">[], today: string): number {
  return hasLiveSitting(sittings, today) ? POLL_LIVE_MS : POLL_IDLE_MS;
}

/** «Dati aggiornati alle HH:MM»: the latest `updated_at` of the sittings (a document of theirs changed); null without one. */
export function updatedAtLabel(sittings: Pick<Sitting, "updated_at">[]): string | null {
  const latest = sittings
    .map((s) => s.updated_at)
    .filter((v): v is string => v !== null)
    .sort()
    .at(-1);
  return latest ? `Dati aggiornati alle ${formatTime(latest)}` : null;
}

/** «Camera» / «Senato», as the columns and the heading name a chamber. */
export const chamberName = (chamber: Chamber) => CHAMBER_LABELS[chamber] ?? chamber;

/** What a sitting's heading and its feed row read: the fields a Sitting and a FeedSitting share. */
export type SittingFacts = Pick<
  Sitting,
  | "chamber"
  | "session_type"
  | "number"
  | "sitting_on"
  | "status"
  | "scheduled_time"
  | "started_time"
  | "ended_time"
  | "ended_on"
  | "documents_count"
  | "updated_at"
>;

/**
 * The heading of a sitting's page: «Senato · Plenaria · Seduta n. 467», and
 * under it «giovedì 15 ottobre 2026 · In corso dalle 10:04 · dati aggiornati
 * alle 15:50» (the last part when `updated_at` is there).
 */
export function sittingHeading(sitting: SittingFacts, today: string): { title: string; line: string } {
  const title = [
    chamberName(sitting.chamber),
    sessionTypeLabel(sitting.session_type),
    sittingNumberLabel(sitting),
  ].join(" · ");
  const parts = [formatWeekdayDate(sitting.sitting_on)];
  if (sitting.session_type === "commissioni") {
    const n = sitting.documents_count;
    const [one, many] = BULLETIN_DOCUMENTS[sitting.chamber];
    parts.push(`${n} ${n === 1 ? one : many}`);
  } else parts.push(sittingStatusLabel(sitting, today), documentsLabel(sitting.documents_count));
  if (sitting.updated_at) parts.push(`dati aggiornati alle ${formatTime(sitting.updated_at)}`);
  return { title, line: parts.join(" · ") };
}

// ---- the row of a sitting in the feed (D7) -----------------------------------------

export type { FeedItem, FeedSitting };
export type SittingDocument = FeedSitting["documents"][number];

/**
 * The first line of a sitting's row in the feed: «Senato · Plenaria · Seduta
 * n. 467 · In corso dalle 10:04»; a bulletin has no state: «Camera ·
 * Commissioni · Bollettino n. 747».
 */
export function sittingRowTitle(sitting: SittingFacts, today: string): string {
  const { title } = sittingHeading(sitting, today);
  return sitting.session_type === "commissioni" ? title : `${title} · ${sittingStatusLabel(sitting, today)}`;
}

/** «2 documenti» for the plenary, «22 comunicati» / «19 resoconti» for a bulletin. */
export function sittingDocumentsLabel(sitting: Pick<Sitting, "chamber" | "session_type" | "documents_count">): string {
  const n = sitting.documents_count;
  if (sitting.session_type !== "commissioni") return documentsLabel(n);
  const [one, many] = BULLETIN_DOCUMENTS[sitting.chamber];
  return `${n} ${n === 1 ? one : many}`;
}

/** How a document of a sitting is named in the row: its organ at the committees, else the label of its type, else its title. */
function sittingDocumentName(doc: SittingDocument): string | null {
  const committee = doc.committee?.trim();
  if (committee) return committee;
  if (doc.item_type) return documentTypeLabel(doc.item_type);
  return doc.title?.trim() || null;
}

/**
 * What moved in a sitting since the visit, under its row: «Nuovo: Resoconto
 * stenografico, Allegato A · Aggiornato: Ordine del giorno», from the
 * documents the row carries with a `change` (a name once, in the order
 * served: newest first). In «News precedenti» (no change on any document)
 * or without a document, how many documents the sitting has.
 */
export function sittingRowExcerpt(
  sitting: Pick<FeedSitting, "chamber" | "session_type" | "documents_count" | "documents">,
): string {
  const named = (change: SittingDocument["change"]) => {
    const names: string[] = [];
    for (const doc of sitting.documents) {
      if (doc.change !== change) continue;
      const name = sittingDocumentName(doc);
      if (name && !names.includes(name)) names.push(name);
    }
    return names;
  };
  const parts: string[] = [];
  const fresh = named("new");
  const updated = named("updated");
  if (fresh.length > 0) parts.push(`Nuovo: ${fresh.join(", ")}`);
  if (updated.length > 0) parts.push(`Aggiornato: ${updated.join(", ")}`);
  return parts.length > 0 ? parts.join(" · ") : sittingDocumentsLabel(sitting);
}

/** The label of the chevron of a sitting's row: it opens the sitting, not a news. */
export const OPEN_SITTING = "Apri la seduta";

/**
 * The row of a sitting in the feed as the NewsCard component takes it: the
 * chips of the source (Parlamento, and its section), the sitting's day with
 * the freshness as the date line, the sitting with its state as the title,
 * what moved as the excerpt; the chevron is a link to the sitting's page
 * (L2), named «Apri la seduta»; no bookmark (they are per document), nothing
 * to open in place.
 */
export function toSittingNewsItem(
  row: FeedSitting,
  today: string,
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
): NewsItem {
  const links = cardLinks(row, tree, search);
  const freshness = row.updated_at ?? row.activity_at;
  const name = row.source?.organisation ?? "Parlamento";
  // the chamber's logo even when the catalogue has no source for the row: camera.it / senato.it
  const badge = sourceBadge(name, row.source?.domain ?? `${row.chamber}.it`);
  return {
    id: row.id,
    source: { name, url: links.organisation, ...badge },
    channel: {
      name: row.source?.section ?? `${chamberName(row.chamber)}, ${sessionTypeLabel(row.session_type)}`,
      url: links.section,
    },
    hasCode: false,
    publishedLabel: `${formatLongDate(row.sitting_on)} (dati aggiornati alle ${formatTime(freshness)})`,
    title: sittingRowTitle(row, today),
    excerpt: [{ text: sittingRowExcerpt(row) }],
    body: [],
    url: row.source?.landing_url ?? "",
    pageUrl: sittingPath(row),
    saved: false,
    chevron: "right",
    savable: false,
    pageLabel: OPEN_SITTING,
  };
}

/** A row of a feed list as NewsCard takes it: a card through toNewsItem, a sitting through toSittingNewsItem. */
export function toFeedNewsItem(
  item: FeedItem,
  today: string,
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
): NewsItem {
  return item.kind === "sitting" ? toSittingNewsItem(item, today, tree, search) : toNewsItem(item, tree, search);
}

// ---- the sittings in Ultime news -----------------------------------------------

/** The sources whose documents belong to a sitting: in a view that names them, the sittings stand in for the cards. */
export const SITTING_SOURCES: ReadonlyMap<string, { chamber: Chamber; sessionType: SessionType }> = new Map([
  ["coro:camera_plenaria", { chamber: "camera", sessionType: "plenaria" }],
  ["coro:camera_commissioni", { chamber: "camera", sessionType: "commissioni" }],
  ["coro:senato_plenaria", { chamber: "senato", sessionType: "plenaria" }],
  ["coro:senato_commissioni", { chamber: "senato", sessionType: "commissioni" }],
]);

/** The view of the four sources of sittings: where /parlamento leads, and the fallback of «Torna alle sedute». */
export const SITTINGS_VIEW_PATH = `/news?${writeNewsParams({ sources: [...SITTING_SOURCES.keys()] })}`;

/** The name of that view, for «Ultime news · Parlamento · Sedute»: the keys are exactly the four sources of sittings. */
export function sittingsViewLabel(keys: string[]): string | null {
  const wanted = [...SITTING_SOURCES.keys()].sort().join("\n");
  return [...new Set(keys)].sort().join("\n") === wanted ? "Parlamento · Sedute" : null;
}

/** One list of sittings of a view: a chamber, and its type or null for both (Plenaria and Commissioni in the filter). */
export interface SittingListScope {
  chamber: Chamber;
  sessionType: SessionType | null;
}

/**
 * What a filter of Ultime news asks for. `lists`: one list of sittings per
 * chamber with a source of sittings in the filter, the Camera's first, both
 * types when the filter names both. `news`: the keys of the other sources,
 * for the lists of news; null for every source (no filter); [] when there is
 * nothing to list as news (only sources of sittings, or ?sources=none).
 */
export function sittingScope(keys: string[] | null): { lists: SittingListScope[]; news: string[] | null } {
  if (keys === null) return { lists: [], news: null };
  const types = new Map<Chamber, Set<SessionType>>();
  const news: string[] = [];
  for (const key of keys) {
    const source = SITTING_SOURCES.get(key);
    if (!source) {
      news.push(key);
      continue;
    }
    const own = types.get(source.chamber) ?? new Set<SessionType>();
    own.add(source.sessionType);
    types.set(source.chamber, own);
  }
  const lists = CHAMBERS.filter((chamber) => types.has(chamber)).map((chamber) => {
    const own = types.get(chamber)!;
    return { chamber, sessionType: own.size === SESSION_TYPES.length ? null : [...own][0]! };
  });
  return { lists, news };
}

/**
 * Where «Torna alle sedute» leads from a sitting's page: the view of
 * Ultime news it was opened from (`state.from`), else the view of the four
 * sources of sittings.
 */
export function backToSittings(state: unknown): string {
  const from = fromOf(state);
  return from !== null && from.startsWith("/news") ? from : SITTINGS_VIEW_PATH;
}

/**
 * The label of the way back from a sitting's page: «Torna alle sedute» when
 * the view it leads to lists sittings (its filter names a source of
 * sittings, sittingScope), «Torna alle news» otherwise (the plain list, where
 * the sitting is one row among the cards).
 */
export function backToSittingsLabel(path: string): string {
  const query = path.split("?")[1] ?? "";
  const { sources } = readNewsParams(new URLSearchParams(query));
  return sittingScope(sources).lists.length > 0 ? "Torna alle sedute" : "Torna alle news";
}

// ---- the card of a bulletin's document -----------------------------------------------

export type FeedCard = components["schemas"]["FeedCard"];

/**
 * The committee meeting of a card as one line («Seduta n. 305 · Pomeridiana
 * · 15:40-16:00»), from the columns Loggione serves with the card (the
 * Senato's committees carry them; the Camera's and the plenary do not): null
 * without any of them.
 */
export function cardMeetingLabel(
  card: Pick<
    FeedCard,
    "sitting_kind" | "sitting_number" | "sitting_shift" | "sitting_started_time" | "sitting_ended_time"
  >,
): string | null {
  return meetingLabel({
    sitting_kind: card.sitting_kind,
    sitting_number: card.sitting_number,
    sitting_shift: card.sitting_shift,
    sitting_started_time: card.sitting_started_time,
    sitting_ended_time: card.sitting_ended_time,
  });
}

// ---- the official page --------------------------------------------------------------

/** The route of a document's official page (GET /items/coro/{id}/html), on the api's origin. */
export function officialPageUrl(apiBase: string, item: Pick<ItemDetail, "origin" | "id">): string {
  return `${apiBase}/api/v1/items/${item.origin}/${encodeURIComponent(item.id)}/html`;
}

/** The anchor of an entry, for the official page at `#anchor`; null when the entry has none. */
export function anchorOf(sections: Pick<Section, "ordinal" | "anchor">[], ordinal: number | null): string | null {
  if (ordinal === null) return null;
  return sections.find((s) => s.ordinal === ordinal)?.anchor ?? null;
}

// ---- the page of a plenary sitting: one block per document, the entries of its index under it (L2) ----

export type SittingSection = components["schemas"]["SittingSection"];
export type SittingStatus = Sitting["status"];

/** One entry under a document's card: an argument or a point of the index, as a link to the document at that point. */
export interface SittingEntry {
  key: string;
  label: string;
  /** How many entries stand above it under the card (0 for a child of the root part). */
  depth: number;
  /** The document's page at the entry (`?voce=<ordinal>`); the document's page alone when the entry has no position. */
  href: string;
  /** A row that heads the ones under it and goes nowhere (the Camera's «SEDE REFERENTE»); false for a link. */
  heading?: boolean;
}

/**
 * One document of a sitting's page, as a closed card: the type of the
 * document (or the meeting, the organ) over «Aggiornato alle 15:50», its
 * official title, how many entries it holds as the excerpt, no chip; and the
 * entries that open inside the card. A document with entries opens in place
 * (chevron down); one without goes to the document (chevron right).
 */
export interface DocumentCard {
  key: string;
  item: NewsItem;
  /** The index of the document: topics and items (the Camera's seats are heading rows); empty: nothing opens. */
  entries: SittingEntry[];
}

/** The excerpt of a closed card that has nothing to open, and the one of a record whose index comes at the end of the sitting. */
export const NO_INDEX = "Senza indice: apri il documento";
export const INDEX_LATER = "Indice disponibile a fine seduta";
export const PDF_ONLY = "Bollettino solo in PDF";
/** The accessible name of the chevron that leads to the document. */
export const OPEN_DOCUMENT = "Apri il documento";

/**
 * The card of a document on the page of a sitting: the news item of the
 * feed with the words of the page. `entries` null: the index did not come, the
 * card says nothing about it.
 */
function documentCard(
  card: FeedCard,
  sittingOn: string,
  heading: string,
  title: string,
  entries: SittingEntry[] | null,
  excerpt: string | null,
  tree: Pick<SourceTree, "active"> | null,
  search: URLSearchParams | undefined,
): DocumentCard {
  const base = toNewsItem(card, tree, search);
  const opens = entries !== null && entries.length > 0;
  const text = entries === null ? null : opens ? excerpt : (excerpt ?? NO_INDEX);
  return {
    key: card.id,
    entries: entries ?? [],
    item: {
      ...base,
      heading,
      publishedLabel: documentUpdatedLabel(card, sittingOn),
      title,
      excerpt: text === null ? [] : [{ text }],
      chevron: opens ? "down" : "right",
      pageLabel: OPEN_DOCUMENT,
    },
  };
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The documents whose entries are never listed: the annexes (Politor lists none for them) and the texts annexed to the agenda. */
const NO_ENTRIES_TYPES = new Set(["allegato_a", "allegato_b", "allegato_b_atti_modificati", "allegato_odg"]);

/**
 * The order of the blocks by the state of the sitting (the brief, §1 L2):
 * convoked, the agenda; in progress, the agenda first, then the record as it
 * grows; closed, the record first, its annexes, the agenda at the end. A
 * type not listed follows, in the order served.
 */
const BLOCK_ORDER: Record<SittingStatus, readonly string[]> = {
  convocata: ["odg_giorno", "ordine_del_giorno", "allegato_odg"],
  in_corso: [
    "odg_giorno",
    "ordine_del_giorno",
    "allegato_odg",
    "resoconto_in_corso",
    "stenografico",
    "allegato_a",
    "allegato_b",
    "allegato_b_atti_modificati",
  ],
  terminata: [
    "stenografico",
    "resoconto_in_corso",
    "allegato_a",
    "allegato_b",
    "allegato_b_atti_modificati",
    "odg_giorno",
    "ordine_del_giorno",
    "allegato_odg",
  ],
};

/**
 * Whether an entry of a document's index is listed under its card, by the
 * document's type and the chamber (the rules after the check against Politor):
 * - the agendas (the Senato's and the Camera's): only the topics and the items
 *   that name an act; the Senato's parser splits the questions in one item per
 *   paragraph, the Camera's has «Relatrice:» lines as items;
 * - the Camera's stenographic record: not the routine entries (Politor hides
 *   them); the Senato's keeps them, its flag is not coherent.
 */
function listed(type: string, chamber: Chamber, section: SittingSection): boolean {
  if (type === "ordine_del_giorno" || type === "odg_giorno")
    return section.level !== "item" || section.act_ref !== null;
  if (type === "stenografico" && chamber === "camera") return !section.in_routine;
  return true;
}

/**
 * The entries of one document, from the reduced index of the sitting
 * (`listed` says which; routine ones only go out of the Camera's record); the one root `part` is the block's title and is not listed, its
 * children stand at depth 0 (with more than one root part, each is a heading
 * row); an entry whose parent is not listed goes out with it. An entry with
 * a position links to the document at that point, one without to the
 * document alone.
 */
function documentEntries(
  card: Pick<FeedCard, "origin" | "id" | "item_type">,
  chamber: Chamber,
  index: SittingSection[],
): SittingEntry[] {
  const type = card.item_type ?? "";
  if (NO_ENTRIES_TYPES.has(type)) return [];
  const own = index.filter((s) => s.item_id === card.id).sort((a, b) => a.ordinal - b.ordinal);
  const roots = own.filter((s) => s.parent_ordinal === null && s.level === "part");
  const titleRoot = roots.length === 1 ? roots[0]!.ordinal : null;
  const path = itemPath(card);
  const depthOf = new Map<number, number>();
  const out: SittingEntry[] = [];
  for (const section of own) {
    if (section.ordinal === titleRoot) {
      depthOf.set(section.ordinal, -1);
      continue;
    }
    const parentDepth =
      section.parent_ordinal === null
        ? -1
        : depthOf.has(section.parent_ordinal)
          ? depthOf.get(section.parent_ordinal)!
          : null;
    if (parentDepth === null || !listed(type, chamber, section)) continue;
    const depth = parentDepth + 1;
    depthOf.set(section.ordinal, depth);
    out.push({
      key: `${card.id}#${section.ordinal}`,
      label: section.title,
      depth,
      href: section.char_start === null ? path : `${path}?voce=${section.ordinal}`,
    });
  }
  return out;
}

/** «Aggiornato alle 15:50» when the document last changed on the sitting's day, «Aggiornato il 15/10/2026 alle 06:00» otherwise. */
export function documentUpdatedLabel(
  card: Pick<FeedCard, "last_changed_at" | "activity_at">,
  sittingOn: string,
): string {
  const at = card.last_changed_at ?? card.activity_at;
  const time = formatTime(at);
  return romeDay(new Date(at)) === sittingOn
    ? `Aggiornato alle ${time}`
    : `Aggiornato il ${formatShortDate(at)} alle ${time}`;
}

/**
 * The cards of a plenary sitting's page: one per document, in the order the
 * state of the sitting asks for (BLOCK_ORDER), each with the entries of its
 * index (documentEntries) that open inside it. The record in progress is
 * left out when the sitting has its stenographic record (Politor shows one
 * «Resoconto»). With `index` null (not arrived, or failed) the cards carry no
 * entry and no word about them; with the index there, a record without an
 * entry says its index comes at the end of the sitting.
 */
export function sittingDocuments(
  detail: Pick<SittingDetail, "sitting" | "documents">,
  index: SittingSection[] | null,
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
): DocumentCard[] {
  const types = new Set(detail.documents.map((d) => d.item_type));
  const order = BLOCK_ORDER[detail.sitting.status];
  const rank = (type: string | null) => {
    const i = type === null ? -1 : order.indexOf(type);
    return i === -1 ? order.length : i;
  };
  return detail.documents
    .filter((card) => !(card.item_type === "resoconto_in_corso" && types.has("stenografico")))
    .map((card, served) => ({ card, served }))
    .sort((a, b) => rank(a.card.item_type) - rank(b.card.item_type) || a.served - b.served)
    .map(({ card }) => {
      const entries = index ? documentEntries(card, detail.sitting.chamber, index) : null;
      const indexLater = entries?.length === 0 && card.item_type !== null && RECORD_TYPES.has(card.item_type);
      const excerpt = entries?.length ? count(entries.length, "punto", "punti") : indexLater ? INDEX_LATER : null;
      const heading = card.item_type ? documentTypeLabel(card.item_type) : (card.title ?? "");
      return documentCard(
        card,
        detail.sitting.sitting_on,
        heading,
        card.title ?? heading,
        entries,
        excerpt,
        tree,
        search,
      );
    });
}

/**
 * The note above the blocks of a convoked sitting: «Convocata alle 9:00:
 * nessun resoconto» («Convocata, nessun resoconto» once the day has passed,
 * as the state reads); null in any other state.
 */
export function convocationNotice(sitting: SittingFacts, today: string): string | null {
  if (sitting.status !== "convocata") return null;
  const state = sittingStatusLabel(sitting, today);
  return state.includes("nessun resoconto") ? state : `${state}: nessun resoconto`;
}

/** The entry `?voce=` of a document's page names, as the reader's first target; null without one that is an ordinal. */
export function voceOf(search: URLSearchParams): number | null {
  const raw = search.get("voce");
  return raw !== null && /^\d{1,6}$/.test(raw) ? Number(raw) : null;
}

// ---- the page of a bulletin: one block per organ, a card per document or meeting, the entries under it (L2) ----

/**
 * What the Senato's meeting part says beyond the card: the part reads
 * «1ª Affari Costituzionali · Sottocommissione per i pareri · seduta n. 134,
 * pomeridiana»; the organ and the number and shift of the meeting are on the
 * card (its heading), what is left («Sottocommissione per i pareri») is a
 * heading row. Null when the part only repeats the card.
 */
function meetingPartDetail(partTitle: string, committee: string | null): string | null {
  const same = (a: string, b: string) =>
    a.replace(/\s+/g, " ").trim().toLowerCase() === b.replace(/\s+/g, " ").trim().toLowerCase();
  const rest = partTitle
    .split(" · ")
    .map((segment) => segment.trim())
    .filter(
      (segment, i) => !(i === 0 && committee !== null && same(segment, committee)) && !/^seduta n\. \d+/i.test(segment),
    );
  return rest.length > 0 ? rest.join(" · ") : null;
}

/**
 * The act's title and its phase («Seguito dell'esame e rinvio»); not twice
 * when the title already ends with it in brackets («… C. 2908. (Seguito
 * dell'esame e rinvio).»): the phase is compared as that suffix, not as any
 * piece of the title.
 */
function withPhase(title: string, phase: string | null): string {
  if (!phase) return title;
  const bare = title.trimEnd().replace(/\.$/, "");
  return bare.endsWith(`(${phase})`) ? title : `${title} · ${phase}`;
}

/**
 * The entries under the card of a bulletin's document, from the reduced
 * index of the sitting. The part is the organ (Camera) or the meeting
 * (Senato) and is not listed, unless the Senato's names more than the card
 * does (meetingPartDetail: a heading row, the rest under it). Camera: a topic
 * is a seat («SEDE REFERENTE», «AUDIZIONI INFORMALI») and heads the acts, each
 * an item with its title and, when it has one, its `phase`. Senato: topics
 * and items are the arguments of the meeting. Each with a position links to
 * the document at that point (`?voce=`), as in the plenary. An entry whose
 * parent is not listed goes out with it.
 */
function committeeEntries(
  card: Pick<FeedCard, "origin" | "id" | "committee">,
  chamber: Chamber,
  index: SittingSection[],
): SittingEntry[] {
  const own = index.filter((s) => s.item_id === card.id).sort((a, b) => a.ordinal - b.ordinal);
  const path = itemPath(card);
  const depthOf = new Map<number, number>();
  const out: SittingEntry[] = [];
  for (const section of own) {
    if (section.level === "part") {
      const detail = chamber === "senato" ? meetingPartDetail(section.title, card.committee) : null;
      if (detail === null) {
        depthOf.set(section.ordinal, -1);
      } else {
        depthOf.set(section.ordinal, 0);
        out.push({ key: `${card.id}#${section.ordinal}`, label: detail, depth: 0, href: path, heading: true });
      }
      continue;
    }
    const parentDepth = section.parent_ordinal === null ? -1 : (depthOf.get(section.parent_ordinal) ?? null);
    if (parentDepth === null) continue;
    const depth = parentDepth + 1;
    depthOf.set(section.ordinal, depth);
    const seat = chamber === "camera" && section.level === "topic";
    out.push({
      key: `${card.id}#${section.ordinal}`,
      label: withPhase(section.title, section.phase),
      depth,
      href: section.char_start === null ? path : `${path}?voce=${section.ordinal}`,
      ...(seat ? { heading: true } : {}),
    });
  }
  return out;
}

/**
 * The cards of a bulletin's page: one list, the documents in the order the
 * api serves them (the official one: `committee_id` at the Camera, the
 * ordinal of the name at the Senato; never reordered here), each a closed
 * card with the entries of its index inside it. The title of the card is the
 * organ (the document's title when it names none); the line of the avatar is
 * the meeting at the Senato (the type of the document when it has none, as a
 * bulletin in PDF) and «Comunicato» at the Camera; the excerpt counts the
 * seats and acts (Camera) or the arguments (Senato). With `index` null (not
 * arrived, or failed) the cards carry no entry.
 */
export function committeeDocuments(
  detail: Pick<SittingDetail, "sitting" | "documents">,
  index: SittingSection[] | null,
  tree: Pick<SourceTree, "active"> | null = null,
  search?: URLSearchParams,
): DocumentCard[] {
  const { chamber, sitting_on: sittingOn } = detail.sitting;
  return detail.documents.map((card) => {
    const committee = card.committee?.trim() || null;
    const pdfOnly = card.item_type === "bollettino_pdf";
    const kind = card.item_type ? documentTypeLabel(card.item_type) : (card.title ?? "");
    const heading =
      chamber === "camera" ? (card.item_type === "comunicato" ? "Comunicato" : kind) : (cardMeetingLabel(card) ?? kind);
    let entries = index && !pdfOnly ? committeeEntries(card, chamber, index) : index ? [] : null;
    // the Senato's meeting that only names its part has nothing to open
    if (chamber === "senato" && entries?.every((e) => e.heading)) entries = [];
    let excerpt: string | null = null;
    if (pdfOnly) excerpt = PDF_ONLY;
    else if (entries?.length) {
      const seats = entries.filter((e) => e.heading).length;
      excerpt =
        chamber === "camera"
          ? [
              seats > 0 ? count(seats, "sede", "sedi") : null,
              entries.length > seats ? count(entries.length - seats, "atto", "atti") : null,
            ]
              .filter((part) => part !== null)
              .join(" · ")
          : count(entries.length - seats, "argomento", "argomenti");
    }
    return documentCard(card, sittingOn, heading, committee ?? card.title ?? kind, entries, excerpt, tree, search);
  });
}
